import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import css from './toolbar.module.css';
import { ListParams } from '../../../../../services/http-client';
import { useService } from '../../../../../services/services';
import Toolbar from './toolbar';
import { route } from '../../../../../services/utils';
import {
  LIVESTOCK_ANIMAL_EVENTS_STARTER_FEED_VIEW,
  LIVESTOCK_ANIMALS_GRAZING_VIEW,
  LIVESTOCK_ANIMALS_OTHER_VIEW,
  LIVESTOCK_ANIMALS_PREGNANCY_CHECK_VIEW,
  LIVESTOCK_ANIMALS_SEPARATION_VIEW,
  LIVESTOCK_ANIMALS_BREEDING_VIEW,
  LIVESTOCK_ANIMALS_WEIGHING_VIEW,
} from '../../../../../router/routes';
import TableVerticalSizeFixer from '../../../../../components/common/table/table-vertical-size-fixer/table-vertical-size-fixer';
import Table from '../../../../../components/common/table/table';
import {
  AnimalEventAnimal,
  AnimalEventTable,
} from '../../../../../interfaces/animal-event';
import Animal from '../../../../../interfaces/animal';
import { useQuery } from '../../../../../hooks/use-query';
import { Link, useHistory } from 'react-router-dom';
import EditIcon from './EditIcon';
import ShowIfAllowed from '../../../../../components/common/show-if-allowed';

export default function AnimalEvents({ animal }: { animal: Animal }) {
  const { t } = useTranslation();
  const [params] = useQuery();
  const transKeyPrefix = 'pages.livestock.animals_and_events.animals.';
  const tp = useCallback((key: string) => t(transKeyPrefix.concat(key)), [t]);
  const { farmId } = useParams<{ farmId: string; id: string }>();
  const { animalEventService } = useService();
  const history = useHistory();
  const currentYear = new Date().getFullYear();
  const defaultFrom = `${currentYear}-01-01`;
  const defaultTo = new Date().toISOString().split('T')[0];

  useEffect(() => {
    if (!params?.from || !params?.to) {
      const search = new URLSearchParams(params);
      if (!params?.from) search.set('from', defaultFrom);
      if (!params?.to) search.set('to', defaultTo);
      history.push({ ...history.location, search: search.toString() });
    }
  }, [params, defaultFrom, defaultTo, history]);

  const request = useCallback(
    (
      _page: number,
      _sort: string,
      _desc: boolean,
      filter: Record<string, any>
    ) => {
      filter = {
        ...filter,
        animal_eid: animal?.animal_id,
      };
      return animalEventService.index(farmId, { ...filter }, true);
    },
    [animalEventService, farmId, animal]
  );

  const viewRoute = useCallback(
    (animalEvent: AnimalEventTable) => {
      const viewRouteMap = new Map([
        ['grazing', LIVESTOCK_ANIMALS_GRAZING_VIEW],
        ['other', LIVESTOCK_ANIMALS_OTHER_VIEW],
        ['pregnancy', LIVESTOCK_ANIMALS_PREGNANCY_CHECK_VIEW],
        ['separation', LIVESTOCK_ANIMALS_SEPARATION_VIEW],
        ['breeding', LIVESTOCK_ANIMALS_BREEDING_VIEW],
        ['weighing', LIVESTOCK_ANIMALS_WEIGHING_VIEW],
        ['starter_feed', LIVESTOCK_ANIMAL_EVENTS_STARTER_FEED_VIEW],
      ]);

      return route(viewRouteMap.get(animalEvent.event_type), {
        farmId,
        id: animalEvent.id,
        mode: 'view',
      });
    },
    [farmId]
  );

  const toolbar = useCallback((params: ListParams): JSX.Element => {
    return <Toolbar params={params} />;
  }, []);

  const getInformation = (_: AnimalEventAnimal, v: AnimalEventTable) => {
    return v.information;
  };

  const getDate = (_: string | null, event: AnimalEventTable) => {
    if (event.date) {
      return event.date;
    } else {
      const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      };
      const dateFrom = new Date(event.date_from).toLocaleDateString(
        'lv-LV',
        options
      );
      const dateTo = new Date(event.date_to).toLocaleDateString(
        'lv-LV',
        options
      );
      return `${dateFrom} - ${dateTo}`;
    }
  };

  return (
    <div className={css['body']}>
      <TableVerticalSizeFixer>
        <Table<AnimalEventTable>
          request={request}
          keyAccessor="id"
          toolbar={toolbar}
          paginate={false}
          columns={[
            {
              accessor: 'date',
              header: tp('date'),
              className: css['strong'],
              valuable: getDate,
            },
            {
              accessor: 'event_type_filter',
              header: tp('event'),
              valuable: (value, event) => {
                return (
                  <div className={'flex items-center gap-2.5'}>
                    {tp(`types.` + value)}{' '}
                    {event.event_type.includes('imported_from_ldc') ? (
                      <span className={'text-[#8B8B8B]'}>(no LDC)</span>
                    ) : (
                      <ShowIfAllowed ability={{ title: 'view animal_event' }}>
                        <Link to={viewRoute(event)}>
                          <EditIcon />
                        </Link>
                      </ShowIfAllowed>
                    )}
                  </div>
                );
              },
            },
            {
              accessor: 'information',
              header: tp('information'),
              className: css['normal'],
              valuable: getInformation,
            },
          ]}
          viewRecordAbility={{ title: 'view animal_event' }}
          filteredTotalsParams={{
            recordName: 'records',
          }}
        />
      </TableVerticalSizeFixer>
    </div>
  );
}
